import { useQuery } from '@tanstack/react-query';
import type {
  AgentApiCandidateProfilesIndex,
  AgentApiCandidateProfilesItem,
} from '@/types/serializers';
import { useState, useCallback, useRef } from 'react';
import { fetchJson } from '@/utils/apiFetch';
import {
  buildBoardQueryParams,
  mergeRecords,
  type BoardsDetailsRequest,
} from './useFetchCandidateBoardsDetails';

type UncategorizedRefetchOptions = {
  nextPage?: boolean;
  resetPage?: boolean;
};

export default function useFetchCandidateBoardsUncategorized(initialQuery?: BoardsDetailsRequest) {
  const [filters, setFilters] = useState<BoardsDetailsRequest>(initialQuery ?? {});
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [version, setVersion] = useState(0);
  const accumulatedRecordsRef = useRef<AgentApiCandidateProfilesItem[]>([]);

  const filterKey = buildBoardQueryParams({ ...filters, page: undefined });

  // ref は取得結果の蓄積先で、キーに含めると取得のたびにキーが変わり同じページを再取得する (Issue #3659)
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const result = useQuery({
    queryKey: ['uncategorizedCandidateProfiles', filterKey, page, version],
    queryFn: async () => {
      const currentPage = pageRef.current;
      const data = await fetchJson<AgentApiCandidateProfilesIndex>(
        `/agent/api/candidate_boards/uncategorized_candidate_profiles.json${buildBoardQueryParams({
          ...filters,
          page: currentPage,
        })}`
      );

      accumulatedRecordsRef.current =
        currentPage === 1
          ? data.records
          : mergeRecords(accumulatedRecordsRef.current, data.records);

      return { ...data, records: accumulatedRecordsRef.current };
    },
    refetchOnWindowFocus: false,
  });

  const { data, isLoading, isFetching, refetch } = result;

  const hasNextPage = data ? data.records.length < data.total_count : false;

  const resetPage = useCallback(() => {
    accumulatedRecordsRef.current = [];
    pageRef.current = 1;
    setPage(1);
  }, []);

  const uncategorizedRefetch = async (options?: UncategorizedRefetchOptions) => {
    if (options?.nextPage) {
      if (!hasNextPage || !data || data.current_page !== pageRef.current) return;
      pageRef.current += 1;
      setPage(pageRef.current);
      return;
    }
    if (options?.resetPage) {
      resetPage();
      setVersion((prev) => prev + 1);
      return;
    }
    await refetch();
  };

  const updateFilters = useCallback(
    (newFilters: Partial<BoardsDetailsRequest>) => {
      resetPage();
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    [resetPage]
  );

  const setKeyword = useCallback((keyword: string) => updateFilters({ keyword }), [updateFilters]);

  const setOrganizationUserIds = useCallback(
    (organization_user_ids: number[]) => updateFilters({ organization_user_ids }),
    [updateFilters]
  );

  return {
    data,
    isLoading,
    isFetching,
    setKeyword,
    setOrganizationUserIds,
    uncategorizedRefetch,
    hasNextPage,
  };
}
